import { createContext, useContext, useState, ReactNode, useRef, Fragment, useEffect } from 'react';

type ModalOptions = {
  hideOnEscapeKey?: boolean;
  hideOnSpaceKey?: boolean;
  onHide?: () => void;
}
type ModalRenderer = (hide: () => void) => React.ReactNode;

interface ModalContextProps {
  hasModal: boolean;
  showModal: (renderer: ModalRenderer, options?: ModalOptions) => () => void;
}

const ModalContext = createContext<ModalContextProps | undefined>(undefined);

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [modals, setModals] = useState<{ id: number; renderer: ModalRenderer; hide: () => void, options: ModalOptions }[]>([]);
  const nextIdRef = useRef(1);

  const showModal = (renderer: ModalRenderer, options: ModalOptions = {}) => {
    const id = nextIdRef.current;
    nextIdRef.current += 1;
    const hide = () => {
      setModals(prev => prev.filter(modal => modal.id !== id));
      options.onHide?.();
    }
    setModals(prev => [...prev, { id, renderer, hide, options }]);
    return hide;
  };

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        const modal = modals[modals.length - 1];
        if (modal && modal.options.hideOnEscapeKey) {
          event.preventDefault();
          modal.hide();
        }
      }
      if (event.key === ' ' || event.key === 'Spacebar') {
        const modal = modals[modals.length - 1];
        if (modal && modal.options.hideOnSpaceKey) {
          event.preventDefault();
          modal.hide();
        }
      }
    }
    window.addEventListener('keydown', handleEscapeKey);
    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    }
  }, [modals]);

  return (
    <ModalContext.Provider value={{ hasModal: modals.length > 0, showModal }}>
      {children}
      {modals.map(modal => (
        <Fragment key={modal.id}>
          {modal.renderer(modal.hide)}
        </Fragment>
      ))}
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};
